import { useState } from 'react';
import { getPlaybackUrl } from '../lib/api/endpoints.js';
import { describeError } from '../lib/errors.js';

/**
 * Plays back a contributed recording from the timeline.
 *
 * The URL is fetched on demand, not up front for every row in the list —
 * it's a five-minute presigned GET (see the API's own playback endpoint), so
 * fetching fifty of them for a page that might show none is wasted work and
 * fifty links that could easily go stale before anyone clicks one.
 */
export function PlaybackButton({ assetId }: { assetId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      setUrl(await getPlaybackUrl(assetId));
    } catch (e) {
      setError(describeError(e).message);
    } finally {
      setLoading(false);
    }
  };

  if (url) {
    return (
      <audio
        controls
        autoPlay
        src={url}
        style={{ height: '2.25rem', maxWidth: '16rem' }}
        // A presigned URL is only valid for five minutes — if the contributor pauses long
        // enough for it to expire, re-fetching a fresh one beats a dead player with no way
        // to recover short of reloading the whole page.
        onError={() => {
          setUrl(null);
          setError('That link expired — try again.');
        }}
      />
    );
  }

  return (
    <span className="stack" style={{ gap: '0.5rem' }}>
      <button type="button" className="btn btn--quiet" onClick={load} disabled={loading}>
        {loading ? 'Loading…' : '▶ Play'}
      </button>
      {error && <span className="meta">{error}</span>}
    </span>
  );
}
